import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from "@angular/core";
import {
	formatTooltipTime,
	getAdvancedChartConfig,
	getCandleResolution,
	unixTimeToLocaleString
} from "../../utils/chart-utils";
import * as Highcharts from "highcharts/highstock";
import HIndicatorsAll from "highcharts/indicators/indicators-all";
import HDragPanes from "highcharts/modules/drag-panes";
import HAnnotationsAdvanced from "highcharts/modules/annotations-advanced";
import HPriceIndicator from "highcharts/modules/price-indicator";
import HFullScreen from "highcharts/modules/full-screen";
import HStockTools from "highcharts/modules/stock-tools";
import HC_exporting from "highcharts/modules/exporting";
import { FiatPrecisionSymbolPipe } from "@modules/core/pipes";
import { AdvancedChartConfig } from "@modules/shared/chart/models/chart-config.model";

HC_exporting(Highcharts);
HStockTools(Highcharts);
HIndicatorsAll(Highcharts);
HDragPanes(Highcharts);
HAnnotationsAdvanced(Highcharts);
HPriceIndicator(Highcharts);
HFullScreen(Highcharts);

@Component({
	selector: "advanced-chart",
	templateUrl: "./advanced-chart.component.html",
	styleUrls: ["./advanced-chart.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class AdvancedChartComponent {
	@Input() public set chartConfig(config: AdvancedChartConfig) {
		if (config) {
			this.initChartOptions(config);
			this.updateSeries(config);
		}
	}
	public isChartUpdated: boolean = false; // Flag used to trigger chart update; will be set back to false once update is done
	public Highcharts: typeof Highcharts = Highcharts;
	public chartOptions: Highcharts.Options | null = null;

	public constructor(
		private changeDetectorRef: ChangeDetectorRef,
		private fiatPrecisionSymbolPipe: FiatPrecisionSymbolPipe
	) {}

	private initChartOptions(config: AdvancedChartConfig): void {
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const that = this;

		this.chartOptions = getAdvancedChartConfig(config.language);
		this.chartOptions.xAxis[0].crosshair.label.formatter = (value) =>
			formatTooltipTime(config.language, value, config.timespan);

		this.chartOptions.xAxis[0].labels.formatter = (args) =>
			unixTimeToLocaleString(config.language, +args.value, config.timespan);

		this.chartOptions.yAxis[0].labels.formatter = (args) =>
			this.fiatPrecisionSymbolPipe.transform(args.value, {
				largeNumber: true,
				round: false,
				currencies: [config.tradable, config.fiat]
			});

		this.chartOptions.yAxis[0].crosshair.label.formatter = (value) =>
			this.fiatPrecisionSymbolPipe.transform(value, {
				largeNumber: true,
				round: false,
				currencies: [config.tradable, config.fiat]
			});

		this.chartOptions.tooltip = {
			shape: "square",
			headerShape: "callout",
			borderWidth: 0,
			shadow: false,
			backgroundColor: "rgba(0,0,0,0)",
			useHTML: true,
			style: {
				zIndex: 100
			},
			positioner: function (width, height, point) {
				return {
					x: point.series.chart.plotLeft - 16,
					y: 0
				};
			},
			formatter: function () {
				const point = this.points[0];
				const color = point.color;
				const style = `style='color: ${color}'`;
				const o = that.getFormattedPrice(config, point.point["open"]);
				const h = that.getFormattedPrice(config, point.point["high"]);
				const l = that.getFormattedPrice(config, point.point["low"]);
				const c = that.getFormattedPrice(config, point.point["close"]);
				const candleResolution = getCandleResolution(config.timespan);
				const assetName = config.tradable.getSymbol();

				return (
					"<div class='advanced-chart-tooltip-container'>" +
					`<div class='advanced-chart-tooltip'>` +
					`<span class='tooltip-asset'>${assetName}, </span>` +
					`<span class='tooltip-timespan'>${candleResolution}</span>` +
					`<span class='tooltip-label'>O</span><span ${style}>` +
					o +
					"</span> " +
					`<span class='tooltip-label'>H</span><span ${style}>` +
					h +
					"</span> " +
					`<span class='tooltip-label'>L</span><span ${style}>` +
					l +
					"</span> " +
					`<span class='tooltip-label'>C</span><span ${style}>` +
					c +
					"</span> " +
					"</div>" +
					"</div>"
				);
			}
		};
	}

	// Custom scroll behavior because Highstock doesn't support scroll natively
	// Code inspired by thread: https://www.highcharts.com/forum/viewtopic.php?t=42538
	public scroll(event: MouseEvent): void {
		const wheelEvent = event as WheelEvent;
		const target = event.target as HTMLElement;

		// Don't alter scroll behavior if popup is open
		if (target.closest(".highcharts-popup")) {
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		const currentChart = this.getChartInstance(); // Find an active chart instance
		const extremes = currentChart.xAxis[0].getExtremes();
		const step = ((extremes.dataMax - extremes.dataMin) / 100) * 5;
		let extremesMax = extremes.max;
		let newMin = extremes.min;

		if (wheelEvent.deltaY < 0) {
			newMin += step;
		} else {
			newMin -= step;
		}

		if (newMin + step >= extremes.dataMin) {
			currentChart.xAxis[0].setExtremes(newMin, extremesMax, true);
		}
	}

	private updateSeries(config: AdvancedChartConfig): void {
		// Reset extremes when chart data is updated
		this.getChartInstance()?.xAxis[0].setExtremes(null, null, false);

		// Map data to [time, o, h, l, c]
		const mappedData = config.data.map((c) => [
			c.getTime().getUnix(),
			c.getOpen(),
			c.getHigh(),
			c.getLow(),
			c.getClose()
		]);

		this.chartOptions.series = [
			{
				name: "Price",
				id: "Price",
				data: mappedData,
				type: "candlestick"
			},
			// Dummy volume data needed to use some indicators
			// TODO: Check if we could just remove all those indicators
			{
				name: "Volume",
				id: "volume",
				data: [],
				type: "column",
				visible: false
			}
		];

		this.isChartUpdated = true;
		this.changeDetectorRef.markForCheck(); // Refresh the view
	}

	private getChartInstance(): Highcharts.Chart {
		// There can be multiple chart instances loaded, but only one is defined
		return Highcharts.charts.find((c) => !!c);
	}

	private getFormattedPrice(config: AdvancedChartConfig, price: number): string {
		return this.fiatPrecisionSymbolPipe.transform(price, {
			id: config.fiat.getId(),
			currencies: [config.fiat, config.tradable],
			removeCurrency: true
		});
	}
}
